import { CommonModule } from '@angular/common';
import { Component, EnvironmentInjector } from '@angular/core';
import { FormBuilder, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { MessageService } from 'primeng/api';
import { ButtonModule } from 'primeng/button';
import { InputTextModule } from 'primeng/inputtext';
import { SkillCategoryService } from '../../../../core/services/skill-category.service';
import { DynamicDialogConfig, DynamicDialogRef } from 'primeng/dynamicdialog';
import { SkillCategory } from '../../../../core/models/skill-category';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { AbstractControl, ValidationErrors } from '@angular/forms';

function skillCategoryLabelValidator(control: AbstractControl): ValidationErrors | null {
  const value = (control.value || '').trim();

  if (!value) {
    return { required: true };
  }

  if (/\s{2,}/.test(value)) {
    return { multipleSpaces: true };
  }

  if (/[\u0600-\u06FF]/.test(value)) {
    return { arabicCharacters: true };
  }

  if (!/[A-Za-z]/.test(value)) {
    return { missingLetter: true };
  }

  return null;
}

@Component({
  selector: 'em-skill-category-form',
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule, InputTextModule, ButtonModule, TranslatePipe],
  templateUrl: './skill-category-form.component.html',
})
export class SkillCategoryFormComponent {
  form!: FormGroup;
  isEditMode = false;
  translations: any = {};
  originalSkillCategory!: SkillCategory;
  duplicateError = false;
  private readonly messageService: MessageService;

  constructor(
    private fb: FormBuilder,
    private skillCategoryService: SkillCategoryService,
    private ref: DynamicDialogRef,
    private config: DynamicDialogConfig,
    private translate: TranslateService,
    environmentInjector: EnvironmentInjector,
  ) {
    this.messageService = environmentInjector.get(MessageService);
  }

  ngOnInit() {
    this.loadTranslations();

    this.form = this.fb.group({
      id: [null],
      label: [null, [Validators.required, skillCategoryLabelValidator]],
    });

    this.form.get('label')?.valueChanges.subscribe(() => {
      this.duplicateError = false;
    });

    if (this.config.data) {
      this.isEditMode = true;
      this.originalSkillCategory = structuredClone(this.config.data);
      this.populateForm(this.config.data);
    }
  }

  loadTranslations() {
    this.translate
      .get([
        'layout.configurations.skillCategories.addSkillCategory.errorMessage.alreadyExists',
        'layout.configurations.skillCategories.addSkillCategory.errorMessage.title',
        'layout.configurations.skillCategories.editSkillCategory.errorMessage.title',
        'layout.configurations.skillCategories.editSkillCategory.errorMessage.alreadyExists',
      ])
      .subscribe((translations) => {
        this.translations = translations;
      });
  }

  populateForm(skillCategory: SkillCategory) {
    this.form.patchValue({
      id: skillCategory.id,
      label: skillCategory.label,
    });
  }

  onSave() {
    if (this.form.invalid) {
      Object.values(this.form.controls).forEach((control) => {
        control.markAsTouched();
      });
      return;
    }

    if (this.isEditMode) {
      this.updateSkillCategory();
    } else {
      this.addSkillCategory();
    }
  }

  addSkillCategory() {
    this.form.patchValue({
      label: this.form.value.label.trim(),
    });
    this.skillCategoryService.addSkillCategory(this.form.value).subscribe({
      next: (skillCategory: SkillCategory) => {
        this.ref.close(skillCategory);
      },
      error: () => {
        this.duplicateError = true;
        this.messageService.add({
          key: 'globalToast',
          severity: 'error',
          summary: this.translations['layout.configurations.skillCategories.addSkillCategory.errorMessage.title'],
          detail: this.translations['layout.configurations.skillCategories.addSkillCategory.errorMessage.alreadyExists'],
        });
      },
    });
  }

  updateSkillCategory() {
    this.form.patchValue({
      label: this.form.value.label.trim(),
    });
    const updatedSkillCategory = this.form.value;
    const comparableOriginal = {
      id: this.originalSkillCategory.id,
      label: this.originalSkillCategory.label,
    };

    const isChanged = JSON.stringify(comparableOriginal) !== JSON.stringify(updatedSkillCategory);
    if (!isChanged) {
      this.ref.close({ type: 'no-change' });
      return;
    }
    this.skillCategoryService.updateSkillCategory(updatedSkillCategory).subscribe({
      next: (skillCategory: SkillCategory) => {
        this.ref.close({ type: 'updated', data: skillCategory });
      },
      error: () => {
        this.duplicateError = true;
        this.messageService.add({
          key: 'globalToast',
          severity: 'error',
          summary: this.translations['layout.configurations.skillCategories.editSkillCategory.errorMessage.title'],
          detail: this.translations['layout.configurations.skillCategories.editSkillCategory.errorMessage.alreadyExists'],
        });
      },
    });
  }

  isInvalid(key: string) {
    const control = this.form.get(key);
    return control?.invalid && (control?.dirty || control?.touched);
  }
}
